import { Modal, Spinner, Text } from "components/common"
import { useQueryUpdate } from "hooks"
import _ from "lodash"
import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import AddSaleStepper from "../components/addSalesForm/components/addSalesStepper/addSalesStepper"
import { useAddSalesContext } from "../addSalesContext/addSalesContext"
import AddSalesForm from "../components/addSalesForm/addSalesForm"
import { flag } from "../utils"

const modalBodyStyle = { padding: "24px", paddingTop: "50px" }

export default function AddSalesModal({ lead, formRef, client, from = {} }) {
  const { t } = useTranslation()
  const [update] = useQueryUpdate()
  const userInfo = useSelector(state => state?.app?.user.info)

  const {
    stateManager,
    updateStateManager,
    updateIsEditable,
    addSalesApis,
    form,
    isImageUploading,
    showNextPayment,
    setClient,
    setLead,
  } = useAddSalesContext()
  const {
    leadSalesInfo,
    getLeadDetails,
    getActiveBookingPlatforms,
    getMortgageAssessment,
    getCountries,
    getTaskTypes,
    getUnits,
    getPaymentList,
    getSaleEvent,
    getPaymentMethod,
    isLoading,
    projects,
    isFetchingLeadDetails,
    isFetchingPaymentMethods,
    error,
    reset,
    modeOfPayments,
    fetchClientAnswers,
    isFetchingProjects,
    getViban,
  } = addSalesApis

  useEffect(() => {
    if (client) {
      setClient(client)
    }
    if (lead) {
      setLead(lead)
    }
  }, [client, lead])

  const onModalVisible = useCallback(() => {
    getLeadDetails({ leadId: lead?.id })
      .unwrap()
      .then(data => {
        updateStateManager({
          unitPrice: data?.unitPrice ?? stateManager?.unitPrice,
          dealPrice: data?.dealPrice ?? stateManager?.dealPrice,
          discountAmount: data?.unitDiscount ?? stateManager?.discountAmount,
          project: data?.project ?? stateManager?.project,
        })
        handleProjectReservations(data)
        data?.contracts?.[0]?.project?.BillingType?.key === "escrow" &&
          data?.contracts?.[0]?.ContractType?.key === "self_finance" &&
          getViban({ contractId: data?.contracts?.[0]?.contractId }, true)
      })
      .catch(error => console.log(error))
    getMortgageAssessment({ leadId: lead?.id })
      .unwrap()
      .catch(error => console.log(error))
    getCountries({ nationalityFlag: true })
    getTaskTypes({
      params: {
        withNextStatuses: 1,
        leadType: lead?.interestedIn,
        ...((userInfo.AgencyConfiguration.Platform.platformKey !== "mubawab" ||
          userInfo.AgencyConfiguration.Platform.platformKey !== "bproperty") &&
          (userInfo.teamType === "sales" ||
            userInfo.teamType === "secondarySales" ||
            userInfo.teamType === "support") && { teamType: userInfo.teamType }),
      },
      extra: { type: "sales" },
    })
  }, [lead?.id, client, userInfo, stateManager])

  const handleProjectReservations = useCallback(
    leadSalesInfo => {
      const paymentModeProps = { flag: flag(leadSalesInfo) }

      const updateStateWithUnit = (unit, project) => {
        const fixedRows = unit?.FeatureCharges?.filter(record => record?.fixed)
        form.setFieldsValue({
          project: project,
          unit: unit,
          deadLine: null,
          amount: null,
        })
        updateStateManager({
          unit: unit,
          ...(leadSalesInfo?.hasMultipleSalesTasks
            ? {}
            : {
                project,
                unitPrice: unit?.unitPrice,
                featureTable: {
                  selectedRowKeys: fixedRows?.map(record => record?.key),
                  selectedPrices: fixedRows?.reduce(
                    (acc, item) =>
                      acc +
                      (item?.amountType === "fixed" ? item?.amount : (item?.amount / 100) * stateManager?.unitPrice),
                    0
                  ),
                  features: fixedRows,
                },
                dealPrice: unit?.unitPrice + (unit?.totalFixedFeatureCharges ?? 0),
              }),
        })
      }
      const handleReservedState = () => {
        const projectId = leadSalesInfo?.ReservationQueuedAgainstLead?.projectId
        const listingId = leadSalesInfo?.ReservationQueuedAgainstLead?.listingId
        getUnits({ projectId }, true)
          .unwrap()
          .then(response => {
            const unit = response?.find(e => e.listingId == listingId)
            const project = leadSalesInfo?.project ?? projects?.find(e => e.projectId == projectId)
            updateStateWithUnit(unit, project)
          })

        paymentModeProps.id = projectId
        leadSalesInfo?.isFirstSalesTask && getActiveBookingPlatforms(projectId, true)
      }
      const handleNonReservedState = () => {
        if (leadSalesInfo?.projectId) {
          paymentModeProps.id = leadSalesInfo?.projectId
        }

        if (leadSalesInfo?.contractId) {
          getPaymentList({
            searchKey: "contractId",
            searchIds: [leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasContractId : leadSalesInfo?.contractId],
            pageOffset: 0,
            pageSize: 50,
            includeStatistics: true,
          })
        }
        if (projects) {
          const option = leadSalesInfo?.project ?? projects?.find(item => item?.projectId === lead?.Project?.projectId)
          if (option) {
            updateStateManager({
              project: option,
            })
            form.setFieldsValue({ project: option })
            getSaleEvent(option?.projectId, true)
            getUnits({ projectId: option.projectId }, true)

            if (option?.projectId) {
              paymentModeProps.id = option?.projectId
            }
            leadSalesInfo?.isFirstSalesTask && getActiveBookingPlatforms(option?.projectId, true)
          }
        }
      }
      if (leadSalesInfo?.isReservedWithReservedStatus) {
        handleReservedState()
      } else if (!leadSalesInfo?.isReserved) {
        handleNonReservedState()
      }
      getPaymentMethod({ ...paymentModeProps })
    },
    [lead, leadSalesInfo, projects, isFetchingProjects]
  )

  const onCancel = () => {
    form.resetFields()
    formRef?.current?.setVisible(false)
    reset()
    update("getLeadDetails", () => null)
    update("getPaymentMethod", () => null)
    updateIsEditable(false)
    updateStateManager({
      project: {},
      unit: {},
      discountAmount: 0,
      unitPrice: 0,
      dealPrice: 0,
      featureTable: {},
    })
  }

  return (
    <Modal
      title={t("Add Sale")}
      ref={formRef}
      width={820}
      okText={t("Submit")}
      disableOkButton={!modeOfPayments?.length || isImageUploading || !showNextPayment}
      cancelText={t("Cancel")}
      loading={isLoading || fetchClientAnswers || isFetchingLeadDetails}
      onOk={form.submit}
      getContainer={false}
      styles={{ body: modalBodyStyle }}
      onModalVisible={onModalVisible}
      footerAlert={error}
      forceRender
      onCancel={onCancel}
      destroyOnClose
    >
      {isFetchingLeadDetails ||
      ((leadSalesInfo?.hasMultipleSalesTasks || leadSalesInfo?.statusIsReserved) && isFetchingPaymentMethods) ? (
        <Spinner />
      ) : (
        <>
          {!leadSalesInfo?.paymentProcessingState ? (
            <>
              <AddSaleStepper />
              <AddSalesForm form={form} formRef={formRef} from={from} />
            </>
          ) : (
            <Text>
              {t(
                "Your current payments are being verified, you can add new payments once the verification is completed"
              )}
            </Text>
          )}
        </>
      )}
    </Modal>
  )
}
